import * as templates from "./templates";
import {MetricsNames} from "../../src-logic/metrics/metrics";
import JSZip from "jszip";
import xml2js from "xml2js";
import {getBase64PrefixOfFile, newEncodeArrayBufferBase64} from "../../src-common/utils/s2k-encodeArrayBufferBase64";
import {getFileExtension} from "../../src-common/utils/s2k-epub-helper";
import {STK_STRING_UNKNOWN} from "../../src-common/constants";

class View {
    constructor() {
        // Wire-up elements for easy access
        this.$title = document.getElementById("s2k-preview-header-title");
        this.$sendButton = document.getElementById("s2k-preview-send");
        this.$backButton = document.getElementById("s2k-preview-back");
        this.$feedbackButton = document.getElementById("s2k-preview-feedback-container");
        this.$content = document.getElementById("s2k-preview-content");

        // Bind callbacks
        this._onSendButtonClick = this._onSendButtonClick.bind(this);
        this._onBackButtonClick = this._onBackButtonClick.bind(this);
        this._onFeedBackButtonClick = this._onFeedBackButtonClick.bind(this);

        // Event handlers from controller
        this.handlers = {
            'feedback': undefined,
            'send': undefined,
            'back': undefined,
        };

        // Event subscriptions
        this.$sendButton.addEventListener('click', this._onSendButtonClick);
        this.$backButton.addEventListener('click', this._onBackButtonClick);
        this.$feedbackButton.addEventListener('click', this._onFeedBackButtonClick);

        // Used for loading images from epub file to the html content
        this.epubImages ={};
        // Input info
        this.author = "";
        this.title = "";
    }

    // ###################################################### Initialization ######################################################
    /**
     * Init the view of preview page
     * @param {boolean} isSelection Whether is preview for selected text
     */
    init(isSelection) {
        this.loadString();
        this.loadA11yString();
        this.configTitle(isSelection);
    }

    /**
     * Unify the width of left part and right part in header to make sure the title is in middle
     */
    unifyWidth () {
        // Get the max width between left part and right part
        let maxWidth = Math.max(this.$sendButton.offsetWidth, this.$backButton.offsetWidth);
        // Set max width to both left and right
        this.$sendButton.style.width = maxWidth + "px";
        this.$backButton.style.width = maxWidth + "px";
    }

    /**
     * Set the title of preview page
     * @param {boolean} isSelection Whether is preview for selected text
     */
    configTitle(isSelection) {
        this.$title.textContent = chrome.i18n.getMessage(isSelection ? "previewSelectionHeaderTitle" : "previewHeaderTitle");
    }

    /**
     * Load localization for all text attributes.
     */
    loadString() {
        $.each($("[s2k-string]"), function () {
            const $n = $(this);
            $n.text(chrome.i18n.getMessage($n.attr("s2k-string")));
        });
    }

    /**
     * Load localization for all a11y-label attributes.
     */
    loadA11yString() {
        $.each($("[s2k-aria]"), function () {
            let $n = $(this);
            $n.attr("aria-label", chrome.i18n.getMessage($n.attr("s2k-aria")));
        });
    }

    /**
     * Set metadata of document to the preview page content
     * @param {{ title: string, author: string, url: string, date: string, source: string, htmlContent: string}} documentMetadata
     * @param {JSZip} epubFile
     */
    async setContent(documentMetadata, epubFile) {
        let htmlContent = documentMetadata.htmlContent;
        // LoadImages for content from epub
        if (epubFile) {
            await this.loadImages(epubFile);
            htmlContent = this.replaceImages(htmlContent);
        }
        // Set content to preview page template
        this.$content.append(...templates.metadataTemplate(documentMetadata));
        this.$content.append(...templates.contentTemplate(htmlContent));
        this.$content.append(...templates.footerTemplate(documentMetadata.url));
        // Monitoring user input length of tile and author
        this.$content.querySelector("#s2k-preview-title").addEventListener('input', function (event) {
            this._onInputLengthChanged("title", event.target.value);
        }.bind(this));
        this.$content.querySelector("#s2k-preview-byline-author").addEventListener('input', function (event) {
            this._onInputLengthChanged("author", event.target.value);
        }.bind(this));
        // Check current title/author first
        this._onInputLengthChanged("author", documentMetadata.author);
        this._onInputLengthChanged("title", documentMetadata.title || documentMetadata.source || STK_STRING_UNKNOWN);
        // Load string and a11y for the element in template
        this.loadString();
        this.loadA11yString();
        // Set title to real middle
        this.unifyWidth();
    }

    /**
     * Enable or disable send button based on current user inputs
     */
    updateSendButtonStatus() {
        // Requirements of send file
        // 1. Title not empty
        // 2. Title and author less or equal to 256
        this.$sendButton.disabled = !(this.title.trim().length > 0 && this.title.length <= 256 && this.author.length <= 256);
    }

    /**
     * Show/Hide inline alert of title/author input view
     * @param {"author" | "title"} sender The sender of the event
     * @param {string} alertText The text to show
     * @param {boolean} hide Whether need hide or show
     */
    showInputInlineAlert(sender, alertText, hide) {
        let alert = this.$content.querySelector("#s2k-preview-" + sender + "-length-alert");
        let inputDiv = this.$content.querySelector(sender === "title" ? "#s2k-preview-title" : "#s2k-preview-byline-author");
        if (hide) {
            // hide alert
            alert.classList.add("s2k-hide");
            inputDiv.classList.remove("alert");
        } else {
            // Get alert text
            let alertTextElement = alert.querySelector('.s2k-preview-input-inline-alert-text');
            // Set alert text
            alertTextElement.textContent = alertText;
            // Show alert
            alert.classList.remove("s2k-hide");
            inputDiv.classList.add("alert");
        }
    }

    /**
     * Since in new version, the file to be previewed is an epub. Images in epub is seperated with HTML content, need to
     * load the image from epub file at first.
     * @param {JSZip} unzippedEpub The unzipped epub file
     */
    async loadImages(unzippedEpub) {
        // Since read image file is an async operation inside `for Each` loop, we need to wait all image loaded finished.
        // So that need a promises array and put all async operation into it to wait until all images is loaded.
        let promises = [];
        // Iterate all images inside image folder
        unzippedEpub.folder("OEBPS/image").forEach(function(relativePath, file) {
            // Generate a promise to do async operation
            let promise = new Promise(function (resolve, reject) {
                // Get base64 encoded image
                file.async('base64').then(function (encodedImage) {
                    // Get base64 prefix to encode the image into correct format
                    let base64Prefix = getBase64PrefixOfFile(file.name || "");
                    // Put base64 encoded image into local array for further use
                    this.epubImages["image/" + relativePath] = base64Prefix + encodedImage;
                    resolve();
                }.bind(this)).catch(function (error) {
                    reject(error);
                });
            }.bind(this));
            // Add async operation promise to array
            promises.push(promise);
        }.bind(this));

        try {
            // Execute all async operation, and block until all operations are completed
            await Promise.all(promises);
        } catch (error) {
            console.error("Error loading images:", error);
        }
    }

    /**
     * Since all images inside HTML from EPUB is a relative path of the image, it cannot be found in the preview page.
     * So that we need to replace them to base64 string we loaded in `load image` method.
     *
     * @param {JSZip} htmlString The string of HTML in epub
     */
    replaceImages(htmlString) {
        let parser = new DOMParser();
        let doc = parser.parseFromString(htmlString, 'text/html');

        let imgElements = doc.querySelectorAll('img');

        imgElements.forEach(function (imgElement) {
            // get src of img tag
            let imgSrc = imgElement.getAttribute('src');

            // check base64
            if (this.epubImages[imgSrc]) {
                // set base64 to src
                imgElement.setAttribute('src', this.epubImages[imgSrc]);
            }
        }.bind(this));

        // return the html string
        return doc.documentElement.outerHTML;
    }

    // ###################################################### Delegate ######################################################
    /**
     * Register the callback to monitoring event of user interaction
     * @param {keyof View['handlers']} event
     * @param {(...args: any) => void} handler
     */
    registerEvent(event, handler) {
        this.handlers[event] = handler;
    }

    /**
     * The event that user click on send button
     */
    _onSendButtonClick() {
        chrome.runtime.sendMessage({
            action: "metrics-add-counter",
            data: {
                metricsName: MetricsNames["preview-send-click"]
            }
        });
        // get title and author
        const $title = this.$content.querySelector("#s2k-preview-title");
        const editedTitle = $title.value;

        const $author = this.$content.querySelector("#s2k-preview-byline-author");
        let editedAuthor = $author.value;
        if (editedAuthor.trim().length === 0) {
            editedAuthor = "";
        }
        if (this.handlers.send ) {
            this.handlers.send(editedTitle, editedAuthor);
        }
    }

    /**
     * The event that user click on back button
     */
    _onBackButtonClick() {
        chrome.runtime.sendMessage({
            action: "metrics-add-counter",
            data: {
                metricsName: MetricsNames["preview-back-click"]
            }
        });
        if (this.handlers.back ) {
            this.handlers.back();
        }
    }

    /**
     * The event that user click on feedback button
     */
    _onFeedBackButtonClick() {
        chrome.runtime.sendMessage({
            action: "metrics-add-counter",
            data: {
                metricsName: MetricsNames["preview-feedback-click"]
            }
        });
        if (this.handlers.feedback ) {
            this.handlers.feedback();
        }
    }

    /**
     * The event that user type into author or tile
     * @param {"author" | "title"} sender The sender of the event
     * @param {string} value The value of user input
     */
    _onInputLengthChanged(sender, value) {
        let alertMetricsName = "";
        value = value || "";
        switch (sender) {
            case "author":
                this.author = value;
                alertMetricsName = MetricsNames["preview-length-alert-author"];
                break;
            case "title":
                this.title = value;
                alertMetricsName = MetricsNames["preview-length-alert-title"];
                break;
        }
        if (value.length > 256) {
            chrome.runtime.sendMessage({
                action: "metrics-add-counter",
                data: {
                    metricsName: alertMetricsName
                }
            });
            // Show alert if the length reach 256, or hide alert if not
            this.showInputInlineAlert(sender, chrome.i18n.getMessage("previewInputLengthLimitAlert"), false);
        } else if (sender === "title" && value.trim().length < 1) {
            // Show alert title is empty
            this.showInputInlineAlert(sender, chrome.i18n.getMessage("previewTitleEmptyAlert"), false);
        } else {
            // Hide alert if every thing is good
            this.showInputInlineAlert(sender, chrome.i18n.getMessage("previewInputLengthLimitAlert"), true);
        }
        // Change send button
        this.updateSendButtonStatus();
    }
}

export {View};
